import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3, _DeviceTableCollection;
import { action, observable, toJS } from 'mobx';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import { DEVICE_FILTER_FIELD_KEYS } from '@kentik/ui-shared/device/deviceTableFilterFields';
import PagedCollection from 'core/model/PagedCollection';
import api from 'core/util/api';
import { deviceIndex } from 'app/util/lookups/deviceIndex';
import DeviceModel from './DeviceModel';
import $setup from '../$setup';
let DeviceTableCollection = (_dec = observable.ref, _dec2 = observable.ref, _class = (_DeviceTableCollection = class DeviceTableCollection extends PagedCollection {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "pageState", _descriptor, this);
    this.serverSortableFields = ['id', 'device_name', 'site_name', 'site_market_name', 'plan_name', 'vendor', 'model', 'os_name', 'os_version', 'device_subtype', 'serial_number', 'ip_address', 'statusDisplay', 'deviceClass', 'flowType', 'percentClassified', 'cpu_util', 'memory_util', 'traffic', 'temperature_last', 'rawFps', 'downSampledFps', 'stats.RibPrefixes', 'stats.RibPrefixes6', 'agent_snmp_credential_name', 'sending_ips'];
    _initializerDefineProperty(this, "serverFilter", _descriptor2, this);
    // Which canSwitchLogic fields should use AND vs OR, kept separate from `serverFilter`'s own keys.
    _initializerDefineProperty(this, "fieldsUsingAndLogic", _descriptor3, this);
    // SORTING
    this.serverSortFn = () => {
      this.resetPageState();
      return this.queuedFetch({
        force: true
      });
    };
  }
  get model() {
    return DeviceModel;
  }

  // Base URL for bulk operations and model read/writes
  get url() {
    return '/api/ui/devices';
  }
  get urlPaths() {
    return {
      pagedFetch: '/api/ui/devices/table'
    };
  }
  get queuedFetchKey() {
    return 'DeviceTableCollection';
  }
  get jsonRoot() {
    return 'data';
  }
  get defaultSortState() {
    return {
      field: 'device_name',
      direction: 'asc'
    };
  }
  get secondarySort() {
    return this.defaultSortState;
  }
  // Prevent stale filters. The base resetState() does not reset these subclass observables.
  resetState() {
    this.serverFilter = DeviceTableCollection.defaultServerFilter;
    this.fieldsUsingAndLogic = new Set();
    super.resetState();
  }
  static stripDeadFilters(filter) {
    return omitBy(filter, value => value === undefined || value === null || value === '' || Array.isArray(value) && value.length === 0);
  }
  toServerFilter(filter) {
    const cleanedFilters = DeviceTableCollection.stripDeadFilters(filter);
    const labelFilterValue = cleanedFilters[DEVICE_FILTER_FIELD_KEYS.LABEL];

    // Handle AND/OR logic unique to label filtering
    if (Array.isArray(labelFilterValue)) {
      cleanedFilters[DEVICE_FILTER_FIELD_KEYS.LABEL] = {
        ids: labelFilterValue,
        logic: this.fieldsUsingAndLogic.has(DEVICE_FILTER_FIELD_KEYS.LABEL) ? 'and' : 'or'
      };
    }
    return cleanedFilters;
  }
  getServerFilter() {
    return this.toServerFilter(toJS(this.serverFilter));
  }
  setServerFilter(filter) {
    const {
      fieldsUsingAndLogic = this.fieldsUsingAndLogic,
      ...rest
    } = filter;
    const nextFilter = DeviceTableCollection.stripDeadFilters(rest);
    const prevFilter = DeviceTableCollection.stripDeadFilters(toJS(this.serverFilter));
    if (isEqual(nextFilter, prevFilter) && isEqual(fieldsUsingAndLogic, this.fieldsUsingAndLogic)) {
      return Promise.resolve();
    }
    this.serverFilter = {
      ...nextFilter
    };
    this.fieldsUsingAndLogic = fieldsUsingAndLogic;
    this.resetPageState();
    return this.queuedFetch({
      force: true
    });
  }
  getSortingParam() {
    const {
      field,
      direction
    } = this.sortState;
    return field ? {
      field,
      direction
    } : undefined;
  }

  // SELECTION & GROUPING

  selectAll() {
    this.selected = this.isAllSelected ? null : this.models.filter(model => model.isSelectable);
  }

  // Overrides Collection's base group() — that version re-fetches via loadMoreItems/this.fetch,
  // which uses a different request shape than this collection's paged/queued fetch.
  group(value) {
    const nextGroupBy = value || null;
    const changed = this.groupBy !== nextGroupBy;
    this.groupBy = nextGroupBy;
    if (changed) {
      this.resetPageState();
      this.queuedFetch({
        force: true
      });
    }
  }

  // PAGINATION

  async goToPage({
    page = 1,
    perPage,
    refetchTotals = true,
    preserveSelection = false
  } = {}) {
    const nextPage = Math.max(page, 1);
    const nextPerPage = Math.max(perPage ?? this.pageState.perPage, 1);
    const offset = (nextPage - 1) * nextPerPage;
    this.setPageState({
      currentPage: nextPage,
      perPage: nextPerPage,
      offset
    });
    return this.queuedFetch({
      includeTotalCount: refetchTotals,
      preserveSelection
    }).then(result => {
      if (preserveSelection) {
        const selectedIds = this.selectedArray.map(model => model.id);
        this.selected = selectedIds.map(id => this.get(id)).filter(model => !!model);
      }
      return result;
    });
  }

  // FETCHING

  async fetch(options = {}) {
    return this.queuedFetch({
      preserveSelection: options.preserveSelection
    }).then(result => {
      if (options.preserveSelection) {
        const selectedIds = this.selectedArray.map(model => model.id);
        this.selected = selectedIds.map(id => this.get(id)).filter(model => !!model);
      }
      return result;
    });
  }
  async queuedFetch(options = {}) {
    const {
      data = {},
      fetchUrl,
      fetchMethod,
      includeTotalCount = true
    } = options;
    const {
      perPage,
      offset
    } = this.pageState;
    const defaultPagination = {
      limit: perPage,
      offset,
      includeTotalCount
    };
    return super.queuedFetch({
      ...options,
      fetchUrl: fetchUrl || this.urlPaths.pagedFetch,
      fetchMethod: fetchMethod || 'post',
      data: {
        filters: this.getServerFilter(),
        sorting: this.getSortingParam(),
        groupBy: typeof this.groupBy === 'string' ? this.groupBy : null,
        pagination: defaultPagination,
        ...data
      }
    });
  }
  // @ts-expect-error needs proper conversion to typed PagedCollection
  processData(response) {
    const {
      totalCount,
      pageCount
    } = this.pageState;
    // @ts-expect-error needs proper conversion to typed PagedCollection
    const result = super.processData(response);
    if (response.pagination?.total_count == null) {
      // server didn't recount (total_count omitted); keep the last known total instead of the reset value
      this.setPageState({
        totalCount,
        pageCount
      });
    }
    this.setPageState({
      pageCountLoaded: true,
      groupedDataFracturedKey: response.groupedDataFracturedKey
    });
    return result;
  }

  // BULK OPERATIONS

  async bulkDelete(options = {}) {
    const {
      selectedArray
    } = this;
    return super.bulkDelete({
      ...options,
      removeFromCollection: false
    }).then(async result => {
      const deleted = selectedArray?.filter(device => !result.hasErrors || !result.errors?.find(errDevice => errDevice.deviceId === device.id));

      // ?? [] so a missing selection never falls through to invalidating every record
      deviceIndex.invalidate(deleted?.map(device => device.id) ?? []);
      deleted?.forEach(model => {
        if (model.isArchived) {
          this.remove(model.id);
        } else {
          model.set('device_status', 'D');
        }
      });
      await this.queuedFetch({
        force: true
      });
      return result;
    });
  }
  async bulkUpdateSite(url, data) {
    this.setRequestStatus('updating');
    try {
      const {
        ids,
        name: site_name,
        site_market_id,
        site_market_name
      } = await api.post(`${url}/bulkUpdateSite`, {
        data
      });
      ids.forEach(id => {
        this.get(id)?.safeSet({
          site_id: data.value,
          site_market_id,
          site_market_name,
          site_name
        });
      });
      // includes devices that aren't on the current page
      deviceIndex.invalidate(ids);
    } finally {
      this.setRequestStatus(null);
    }
  }
  async bulkUpdatePlan(url, data) {
    this.setRequestStatus('updating');
    try {
      const {
        ids,
        plan_name
      } = await api.post(`${url}/bulkUpdatePlan`, {
        data
      });
      const decrements = {};
      ids.forEach(id => {
        const prevPlan = this.get(id)?.get('plan_id');
        decrements[prevPlan] = (decrements[prevPlan] || 0) + 1;
        this.get(id)?.safeSet({
          plan_id: data.value,
          plan_name
        });
      });
      $setup.plans = $setup.plans.map(plan => {
        if (String(plan.id) === String(data.value)) {
          plan.device_count += ids.length;
        }
        if (decrements[plan.id]) {
          plan.device_count -= decrements[plan.id];
        }
        return plan;
      });
    } finally {
      this.setRequestStatus(null);
    }
  }
  async bulkUpdate(name, value) {
    if (name === 'kentik_agent_snmp_options.agent_id') {
      return super.bulkUpdate(name, value);
    }
    const {
      url
    } = this;
    const {
      selectedIds
    } = this;
    if (!selectedIds?.length || typeof value !== 'string' && typeof value !== 'number') {
      return null;
    }
    const data = {
      ids: selectedIds,
      value
    };
    return name === 'site_id' ? this.bulkUpdateSite(url, data) : this.bulkUpdatePlan(url, data);
  }
}, _DeviceTableCollection.defaultServerFilter = {
  isArchived: true
}, _DeviceTableCollection), _descriptor = _applyDecoratedDescriptor(_class.prototype, "pageState", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {
      offset: 0,
      pageCount: 1,
      currentPage: 1,
      perPage: 50,
      totalCount: 0,
      pageCountLoaded: false,
      groupedDataFracturedKey: undefined
    };
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "serverFilter", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return DeviceTableCollection.defaultServerFilter;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "fieldsUsingAndLogic", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return new Set();
  }
}), _applyDecoratedDescriptor(_class.prototype, "resetState", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetState"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setServerFilter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setServerFilter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectAll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "group", [action], Object.getOwnPropertyDescriptor(_class.prototype, "group"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "processData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "processData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "bulkUpdatePlan", [action], Object.getOwnPropertyDescriptor(_class.prototype, "bulkUpdatePlan"), _class.prototype), _class);
export default DeviceTableCollection;